import { ComponentType, lazy, LazyExoticComponent } from "react";

// Number of in-place import retries before falling back to a full reload.
const MAX_IMPORT_RETRIES = 2;
// Delay between import retries; a transient network blip usually clears quickly.
const RETRY_DELAY_MS = 500;
// sessionStorage flag guarding against a reload loop when a chunk is genuinely
// gone (e.g. a redeploy replaced the hashed filename referenced by a stale
// index.html held in the HTTP cache).
const CHUNK_RELOAD_FLAG = "collins:chunkReloadAttempted";

// Indirection around window.location.reload so the reload can be stubbed in
// tests (jsdom's window.location is not reconfigurable).
export const pageReloader = {
  reload: (): void => {
    window.location.reload();
  }
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const clearReloadGuard = (): void => {
  try {
    window.sessionStorage?.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    // sessionStorage can be unavailable (private mode / RN WebView); ignore.
  }
};

const hasReloadedForChunk = (): boolean => {
  try {
    return window.sessionStorage?.getItem(CHUNK_RELOAD_FLAG) === "true";
  } catch {
    return false;
  }
};

// Returns false when the guard cannot be persisted (storage unavailable), so
// callers can skip the reload rather than risk a loop.
const markReloadedForChunk = (): boolean => {
  try {
    window.sessionStorage?.setItem(CHUNK_RELOAD_FLAG, "true");
    return true;
  } catch {
    return false;
  }
};

/**
 * Loads a dynamic import, retrying a few times on failure before falling back
 * to a single full page reload. A lazy chunk that fails to load on a cold cache
 * would otherwise leave the <Suspense> fallback (PageLoader) up indefinitely.
 */
export async function retryDynamicImport<T>(
  factory: () => Promise<T>,
  retries: number = MAX_IMPORT_RETRIES,
  delayMs: number = RETRY_DELAY_MS
): Promise<T> {
  try {
    const loadedModule = await factory();
    // A successful import means chunk serving is healthy again, so clear the
    // guard to let a future stale chunk trigger its own reload.
    clearReloadGuard();
    return loadedModule;
  } catch (error) {
    if (retries > 0) {
      await wait(delayMs);
      return retryDynamicImport(factory, retries - 1, delayMs);
    }

    // Retries exhausted. A chunk that never loads is usually stale after a
    // redeploy, so force a single reload to pull the current index.html and
    // its fresh chunk hashes. The guard prevents a reload loop if the chunk is
    // still missing afterwards.
    if (!hasReloadedForChunk() && markReloadedForChunk()) {
      pageReloader.reload();
      // Keep the Suspense fallback up while the page reloads rather than
      // surfacing the error mid-navigation.
      return new Promise<T>(() => {});
    }

    throw error;
  }
}

/**
 * Drop-in replacement for React.lazy that retries the dynamic import and, as a
 * last resort, reloads once to recover from stale chunks after a deploy.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => retryDynamicImport(factory));
}
